import { useEffect, useMemo, useRef, useState } from "react";
import {
    useReactTable,
    getCoreRowModel,
    RowSelectionState,
    Updater,
} from "@tanstack/react-table";
import isEmpty from "lodash/isEmpty";
import {
    getColumnsDefinitions,
    getColumnsVisibility,
} from "./core/helpers/columnAdapter";
import {
    Direction,
    ITableProps,
    SortingState,
    RowSelectionType,
    IColumnMeta,
    RowsSelection,
    RowsState,
} from "./table.types";

export function useTable<T>({
    data,
    columns,
    hideColumns = [],
    pagination,
    onPageChange,
    onSort,
    selectionMode,
    onSelectionChange,
    rowUniqueKey,
    getCheckBoxState,
}: Pick<
    ITableProps<T>,
    | "data"
    | "hideColumns"
    | "pagination"
    | "onPageChange"
    | "columns"
    | "onSort"
    | "selectionMode"
    | "onSelectionChange"
    | "rowUniqueKey"
    | "getCheckBoxState"
>) {
    const [showScrollShadow, setShowScrollShadow] = useState(false);
    const [rowSelection, setRowSelection] = useState<RowsSelection>({});
    const [sortingState, setSortingState] = useState<SortingState | undefined>(
        () => {
            if (onSort)
                return {
                    columnIndex: -1,
                    direction: Direction.DEFAULT,
                };
            return undefined;
        }
    );
    const { pageSize = 10, totalRows = -1, pageNumber = 1 } = pagination || {};
    const shouldPaginate = !isEmpty(pagination) && totalRows > pageSize;
    const tableContainer = useRef<HTMLDivElement>(null);
    const visitedRows = useRef<RowsState>({});

    const updateInitialSelectedRows = () => {
        if (!getCheckBoxState) return rowSelection;
        return data.reduce(
            (selectedRows: RowsSelection, currentRow) => {
                const rowId = currentRow[rowUniqueKey];

                // update default state only if it has been not set previously
                if (!visitedRows[rowId]) {
                    selectedRows[rowId] =
                        getCheckBoxState(currentRow).isSelected ??
                        selectedRows[rowId];
                }

                visitedRows[rowId] = true;
                return selectedRows;
            },
            { ...rowSelection }
        );
    };

    const updateSelectedRows = (updater: Updater<RowSelectionState>) => {
        if (typeof updater === "function") {
            const updatedRowsSelection = updater(rowSelection);
            const currentPageRows = table.getPaginationRowModel().rows;

            // handle disabled rows, i.e retain row selection even on toggle
            if (getCheckBoxState) {
                for (const row of currentPageRows) {
                    const rowState = getCheckBoxState(row.original);
                    if (rowState?.isDisabled) {
                        updatedRowsSelection[row.id] = !!rowState.isSelected;
                    }
                }
            }

            // extracts rows that have been changed in the current page
            const changedSelections = currentPageRows
                .filter(
                    (row) =>
                        !!updatedRowsSelection[row.id] !== row.getIsSelected()
                )
                .reduce(
                    (changedSelections: RowsSelection, row) => ({
                        ...changedSelections,
                        [row.id]: !!updatedRowsSelection[row.id],
                    }),
                    {}
                );

            setRowSelection({ ...updatedRowsSelection });
            onSelectionChange?.(
                Object.keys(updatedRowsSelection).filter(
                    (rowId) => !!updatedRowsSelection[rowId]
                ),
                changedSelections
            );
        }
    };

    useEffect(() => {
        setRowSelection((prev) => {
            return { ...prev, ...updateInitialSelectedRows() };
        });
    }, [data]);

    const columnsDefinitions = useMemo(
        () =>
            getColumnsDefinitions<T>(
                columns,
                selectionMode === RowSelectionType.MULTI
            ),
        [columns]
    );

    const columnVisibility = useMemo(
        () => getColumnsVisibility(hideColumns),
        [hideColumns]
    );

    useEffect(() => {
        if (tableContainer.current) {
            const { scrollWidth, clientWidth } = tableContainer.current;
            setShowScrollShadow(scrollWidth > clientWidth);
        }
    }, [hideColumns]);

    function handlePageChange(pageNumber: number, pageSize: number) {
        onPageChange?.(
            {
                pageNumber,
                pageSize,
                offset: (pageNumber - 1) * pageSize,
            },
            sortingState
        );
    }

    function handleSort(columnIndex: number) {
        const direction =
            sortingState?.columnIndex === columnIndex &&
            sortingState?.direction === Direction.ASC
                ? Direction.DESC
                : Direction.ASC;
        setSortingState({ columnIndex: columnIndex, direction: direction });
        const { id, dataKey } = columnsDefinitions[columnIndex]
            ?.meta as IColumnMeta<T>;
        onSort?.(direction, id ?? dataKey, data);
    }

    const table = useReactTable<T>({
        data,
        columns: columnsDefinitions,
        state: {
            columnVisibility,
            rowSelection,
        },
        enableMultiRowSelection: selectionMode === RowSelectionType.MULTI,
        enableRowSelection: (row) =>
            !getCheckBoxState?.(row.original).isDisabled,
        getCoreRowModel: getCoreRowModel(),
        onRowSelectionChange: updateSelectedRows,
        getRowId: (originalRow) => originalRow?.[rowUniqueKey],
    });

    return {
        table,
        shouldPaginate,
        pageNumber,
        totalRows,
        pageSize,
        handlePageChange,
        showScrollShadow,
        tableContainer,
        handleSort,
        sortingState,
    };
}
